/**
 * RouteErrorBoundary Component
 *
 * GW-4610: React Error Boundary for catching JavaScript errors in route components
 * and displaying a user-friendly fallback UI instead of a blank screen.
 *
 * GW-6397: Detects lazy-loaded chunk fetch failures (stale deployment / network
 * failure) and either auto-reloads once or shows a "Refresh page" button so the
 * user always has a recovery path instead of a blank/broken page.
 *
 * GW-6411: Added data-testid="chunk-error-reload-btn" to the reload button for
 * testability in automated and E2E tests.
 */

import React, { Component, ReactNode } from 'react';

import { Button } from '@gw/ui';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Outlet } from 'react-router-dom';

import { AppLayout } from '@/components/layouts/AppLayout';

export const CHUNK_ERROR_RELOAD_KEY = 'gw_chunk_error_reloaded';

/**
 * Returns true when the error message matches the browser-native
 * "Failed to fetch dynamically imported module" pattern that fires
 * when a lazy-loaded chunk URL returns 404 (stale deployment) or
 * when there is a transient network failure.
 */
export function isChunkLoadError(error: Error): boolean {
  const msg = error.message ?? '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    // Vite's own fallback wording
    msg.includes('Unable to preload CSS for') ||
    // Safari
    msg.toLowerCase().includes('failed to load module script')
  );
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

interface RouteErrorBoundaryProps {
  children?: ReactNode;
}

export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    const chunkError = isChunkLoadError(error);

    if (chunkError) {
      const alreadyReloaded = sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY) === 'true';

      if (!alreadyReloaded) {
        // Mark that we are about to reload so we do not loop.
        sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, 'true');
        window.location.reload();
        // Return the error state — the reload will interrupt the current render
        // so this state will never actually be painted when auto-reload fires.
        return { hasError: true, error, isChunkError: true };
      }
    }

    return { hasError: true, error, isChunkError: chunkError };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('RouteErrorBoundary caught an error:', error, errorInfo);
    if (isChunkLoadError(error)) {
      Sentry.captureException(error, {
        tags: { error_type: 'chunk_load_failure' },
        extra: { componentStack: errorInfo.componentStack },
      });
    }
  }

  handleRetry = () => {
    // Clear the reload guard before the manual retry so a subsequent
    // chunk error on the same session can auto-reload again if needed.
    sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY);
    this.setState({ hasError: false, error: null, isChunkError: false });
  };

  handleReload = () => {
    sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY);
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { isChunkError, error } = this.state;

      if (isChunkError) {
        return (
          <AppLayout variant="bare">
            <div className="flex min-h-[400px] flex-col items-center justify-center p-8 text-center">
              <div className="bg-primary/10 mb-6 flex h-16 w-16 items-center justify-center rounded-full">
                <RefreshCw className="text-primary h-8 w-8" />
              </div>

              <h2 className="text-foreground mb-4 text-2xl font-semibold">New version available</h2>

              <p className="text-muted-foreground mb-6 max-w-md">
                General Wisdom has been updated. Refresh the page to load the latest version.
              </p>

              <Button
                onClick={this.handleReload}
                variant="default"
                className="flex items-center gap-2"
                data-testid="chunk-error-reload-btn"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh page
              </Button>
            </div>
          </AppLayout>
        );
      }

      return (
        <AppLayout variant="bare">
          <div className="flex min-h-[400px] flex-col items-center justify-center p-8 text-center">
            <div className="bg-destructive/10 mb-6 flex h-16 w-16 items-center justify-center rounded-full">
              <AlertTriangle className="text-destructive h-8 w-8" />
            </div>

            <h2 className="text-foreground mb-4 text-2xl font-semibold">Something went wrong</h2>

            <p className="text-muted-foreground mb-6 max-w-md">
              An unexpected error occurred while loading this page. Please try again or contact
              support if the problem persists.
            </p>

            <Button
              onClick={this.handleRetry}
              variant="default"
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </Button>

            {import.meta.env.DEV && error && (
              <details className="mt-6 w-full max-w-md">
                <summary className="text-muted-foreground cursor-pointer text-sm">
                  Error Details (Development Only)
                </summary>
                <pre className="bg-muted text-muted-foreground mt-2 overflow-auto rounded p-3 text-left text-xs">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        </AppLayout>
      );
    }

    return this.props.children ?? <Outlet />;
  }
}
